import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import confetti from 'canvas-confetti';
import PropTypes from 'prop-types';
import { Volume2, RotateCcw, Trophy, Star, Home, Target, TrendingUp, TrendingDown } from 'lucide-react';
import useAudio from '../../hooks/useAudio';
import useFirebase from '../../hooks/useFirebase';
import { Link } from 'react-router-dom';
import useAnalytics from '../../hooks/useAnalytics';

// Scoring constants
const SCORING_CONSTANTS = {
  BASE_POINTS: 20,
  STREAK_MULTIPLIER: 1.5,
  PERFECT_STREAK_BONUS: 100, // Bonus for streaks of 10+
  MEGA_STREAK_BONUS: 300,    // Bonus for streaks of 20+
  QUESTIONS_FOR_STARS: {
    1: 10,  // 1 star for 10 correct
    2: 25,  // 2 stars for 25 correct  
    3: 50   // 3 stars for 50 correct
  }
};

const BiggerSmallerCountryGame = () => {
  // Initialize hooks - using flag guesser for countries data, but separate storage for scores
  const { saveSessionData } = useAnalytics();
  const { 
    levelsData,
    isLoading: countriesLoading,
    error: countriesError
  } = useFirebase('arabicFlagGuesser');

  // Separate hook for this game's scoring data - creates its own Firebase collection
  const {
    bestScores,
    saveLevelData,
    resetAllProgress
  } = useFirebase('biggerSmallerCountries');

  const {
    playCorrectSound,
    playIncorrectSound,
    playPerfectSound,
    playStreakSound,
    playWhooshSound,
    playStarSound,
    startBackgroundMusic,
    AudioElements
  } = useAudio();

  // Audio refs
  const audioRef = useRef(null);
  const audioTimeoutRef = useRef(null);
  
  // Game state
  const [gamePhase, setGamePhase] = useState('menu'); // 'menu', 'playing', 'gameOver'
  const [countries, setCountries] = useState([]);
  const [currentCountries, setCurrentCountries] = useState([null, null]);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [isAnswered, setIsAnswered] = useState(false);
  const [showResult, setShowResult] = useState(false);

  const [sessionStartTime, setSessionStartTime] = useState(Date.now());
  const [sessionMistakes, setSessionMistakes] = useState([]);
  
  // Scoring state
  const [score, setScore] = useState(0);
  const [currentStreak, setCurrentStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [totalCorrect, setTotalCorrect] = useState(0);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [stars, setStars] = useState(0);
  const [gameStartTime, setGameStartTime] = useState(Date.now());
  
  // UI state
  const [feedback, setFeedback] = useState('');
  const [celebrations, setCelebrations] = useState(false);
  const [sparkles, setSparkles] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(null); // Track which country audio is playing
  const [showInstructions, setShowInstructions] = useState(false);
  const [showGameStats, setShowGameStats] = useState(false);
  const [showResetConfirmation, setShowResetConfirmation] = useState(false);

  // Process countries data when loaded
  useEffect(() => {
    if (levelsData && Object.keys(levelsData).length > 0) {
      const countriesArray = Object.values(levelsData)
        .filter(country => country && country.population && country.population > 0)
        .map(country => ({
          id: country.countryCode,
          nameEnglish: country.names?.english || 'Unknown',
          nameArabic: country.names?.arabic || 'غير معروف',
          population: country.population || 0,
          flag: country.flag || '/pics/flags/default.svg',
          audioURL: country.audio?.country?.url || null
        }));
      
      setCountries(countriesArray);
      console.log(`Loaded ${countriesArray.length} countries for comparison game`);
    }
  }, [levelsData]);

  // Audio playback with proper cleanup
  const playCountryAudio = useCallback(async (countryData, countryIndex) => {
    if (!countryData?.audioURL) {
      console.log(`No audio available for ${countryData?.nameEnglish}`);
      return;
    }

    // Clear any existing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    
    if (audioTimeoutRef.current) {
      clearTimeout(audioTimeoutRef.current);
      audioTimeoutRef.current = null;
    }

    setAudioPlaying(countryIndex);

    try {
      const audio = new Audio();
      audioRef.current = audio;
      
      audioTimeoutRef.current = setTimeout(() => {
        setAudioPlaying(null);
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current = null;
        }
      }, 5000);
      
      const handleEnded = () => {
        setAudioPlaying(null);
        if (audioTimeoutRef.current) {
          clearTimeout(audioTimeoutRef.current);
          audioTimeoutRef.current = null;
        }
      };
      
      const handleError = () => {
        console.warn('Audio failed to load:', countryData.audioURL);
        setAudioPlaying(null);
      };
      
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      
      audio.volume = 0.8;
      audio.src = countryData.audioURL;
      await audio.play();
      
    } catch (error) {
      console.error('Failed to play audio:', error);
      setAudioPlaying(null);
    }
  }, []);

  // Generate new country pair
  const generateNewPair = useCallback(() => {
    if (countries.length < 2) return;
    
    // Get two different random countries
    const shuffled = [...countries].sort(() => 0.5 - Math.random());
    const pair = [shuffled[0], shuffled[1]];
    
    setCurrentCountries(pair);
    setSelectedAnswer(null);
    setIsAnswered(false);
    setShowResult(false);
    setFeedback('');
  }, [countries]);

  // Celebration effects
  const lastCelebrationRef = useRef(0);
  const triggerCelebration = useCallback((isStreak = false, earnedStars = 0) => {
    const now = Date.now();
    if (now - lastCelebrationRef.current < 500) return;
    lastCelebrationRef.current = now;
    
    setCelebrations(true);
    setSparkles(true);
    
    let colors = ['#00ff88', '#00ddff', '#ff6b9d'];
    let particleCount = 30;
    let shapes = ['circle'];
    
    if (earnedStars > 0) {
      colors = ['#FFD700', '#FFA500'];
      shapes = ['star'];
      particleCount = 60;
      playStarSound();
    } else if (isStreak) {
      colors = ['#FFD700', '#FFA500', '#ff6347'];
      particleCount = 45;
      playStreakSound();
    } else {
      playCorrectSound();
    }
    
    confetti({
      particleCount,
      spread: 60,
      origin: { y: 0.7 },
      colors,
      shapes,
      gravity: 1.2,
      drift: 0
    });

    setTimeout(() => {
      setCelebrations(false);
      setSparkles(false);
    }, 1500);
  }, [playStarSound, playStreakSound, playCorrectSound]);

  // Handle answer selection
  const handleAnswer = useCallback((selectedCountryIndex) => {
    if (isAnswered || !currentCountries[0] || !currentCountries[1]) return;

    const [country1, country2] = currentCountries;
    const isCorrect = (selectedCountryIndex === 0 && country1.population >= country2.population) ||
                     (selectedCountryIndex === 1 && country2.population >= country1.population);

    setSelectedAnswer(selectedCountryIndex);
    setIsAnswered(true);
    setShowResult(true);
    setTotalQuestions(prev => prev + 1);

    if (isCorrect) {
      const newStreak = currentStreak + 1;
      const newTotalCorrect = totalCorrect + 1;
      
      let points = SCORING_CONSTANTS.BASE_POINTS;
      if (newStreak >= 5) {
        points = Math.round(points * Math.pow(SCORING_CONSTANTS.STREAK_MULTIPLIER, Math.min(newStreak - 4, 10)));
      }
      
      // Special streak bonuses
      let bonusPoints = 0;
      if (newStreak === 10) bonusPoints = SCORING_CONSTANTS.PERFECT_STREAK_BONUS;
      if (newStreak === 20) bonusPoints = SCORING_CONSTANTS.MEGA_STREAK_BONUS;
      
      setScore(prev => prev + points + bonusPoints);
      setCurrentStreak(newStreak);
      setBestStreak(prev => Math.max(prev, newStreak));
      setTotalCorrect(newTotalCorrect);
      
      // Calculate stars
      let newStars = 0;
      if (newTotalCorrect >= SCORING_CONSTANTS.QUESTIONS_FOR_STARS[3]) newStars = 3;
      else if (newTotalCorrect >= SCORING_CONSTANTS.QUESTIONS_FOR_STARS[2]) newStars = 2;
      else if (newTotalCorrect >= SCORING_CONSTANTS.QUESTIONS_FOR_STARS[1]) newStars = 1;
      
      const earnedNewStar = newStars > stars;
      setStars(newStars);
      
      setFeedback('correct');
      triggerCelebration(newStreak >= 5, earnedNewStar ? 1 : 0);
      
    } else {

      setSessionMistakes(prev => [...prev, {
        timestamp: Date.now(),
        type: 'wrong_country_comparison',
        attempted: selectedCountryIndex === 0 ? currentCountries[0].nameEnglish : currentCountries[1].nameEnglish,
        correct: currentCountries[0].population >= currentCountries[1].population ? currentCountries[0].nameEnglish : currentCountries[1].nameEnglish,
        populations: {
          country1: { name: currentCountries[0].nameEnglish, population: currentCountries[0].population },
          country2: { name: currentCountries[1].nameEnglish, population: currentCountries[1].population }
        }
      }]);

      setCurrentStreak(0);
      setFeedback('incorrect');
      playIncorrectSound();
    }

    // Auto advance after delay
    setTimeout(() => {
      generateNewPair();
    }, isCorrect ? 2500 : 3000);
  }, [isAnswered, currentCountries, currentStreak, totalCorrect, stars, triggerCelebration, playIncorrectSound, generateNewPair]);

  // Game controls
  const startGame = useCallback(() => {
    setSessionStartTime(Date.now()); 
    setSessionMistakes([]);
    setGamePhase('playing');
    setScore(0);
    setCurrentStreak(0);
    setTotalCorrect(0);
    setTotalQuestions(0);
    setStars(0);
    setGameStartTime(Date.now());
    generateNewPair();
  }, [generateNewPair]);

  const endGame = useCallback(() => {
    setGamePhase('gameOver');
    
    // Save high score data
    const accuracy = totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0;
    const gameTime = Date.now() - gameStartTime;
    
    const scoreData = {
      totalScore: score,
      accuracy,
      stars,
      bestStreak,
      totalCorrect,
      totalQuestions,
      gameTime: Math.round(gameTime / 1000),
      completedAt: new Date().toISOString()
    };

    saveLevelData(1, scoreData); // Using level 1 for this game
    const sessionData = {
      gameType: 'biggerSmallerCountries',
      level: 1,
      sessionStartTime,
      sessionDuration: Math.round((Date.now() - sessionStartTime) / 1000),
      totalScore: score,
      accuracy,
      attempts: totalQuestions,
      correctAnswers: totalCorrect,
      mistakes: sessionMistakes,
      starsEarned: stars,
      bestStreak
    };
    saveSessionData(sessionData);
  }, [totalQuestions, totalCorrect, score, stars, bestStreak, gameStartTime, saveLevelData]);

  const goToMenu = useCallback(() => {
    setGamePhase('menu');
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setAudioPlaying(null);
  }, []);

  const resetProgress = useCallback(() => {
    setShowResetConfirmation(true);
  }, []);

  const confirmReset = useCallback(() => {
    resetAllProgress();
    setShowResetConfirmation(false);
    setBestStreak(0);
  }, [resetAllProgress]);

  // Initialize game
  useEffect(() => {
    if (countries.length >= 2 && gamePhase === 'playing' && !currentCountries[0]) {
      generateNewPair();
    }
  }, [countries, gamePhase, currentCountries, generateNewPair]);

  // Start background music
  useEffect(() => {
    const musicTimer = setTimeout(() => {
      startBackgroundMusic();
    }, 1000);
    return () => clearTimeout(musicTimer);
  }, [startBackgroundMusic]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (audioTimeoutRef.current) {
        clearTimeout(audioTimeoutRef.current);
      }
      confetti.reset();
    };
  }, []);

  // Components
  const StarDisplay = React.memo(({ count, size = "text-2xl" }) => (
    <div className={`flex justify-center gap-1 ${size}`}>
      {[1, 2, 3].map(star => (
        <span 
          key={star} 
          className={`drop-shadow-lg transition-all duration-300 ${
            star <= count ? 'text-yellow-400 animate-pulse' : 'text-gray-300'
          }`}
        >
          ⭐
        </span>
      ))}
    </div>
  ));

  const CountryCard = React.memo(({ country, index, isSelected, isCorrect, showAnswer }) => {
    if (!country) return null;

    let cardClass = "bg-gradient-to-br from-blue-400 to-purple-500 rounded-3xl p-6 shadow-xl border-4 border-white transition-all duration-300 cursor-pointer transform hover:scale-105 ";
    
    if (showAnswer) {
      if (isCorrect) {
        cardClass = "bg-gradient-to-br from-green-400 to-green-500 rounded-3xl p-6 shadow-xl border-4 border-green-300 transition-all duration-300 ";
      } else if (isSelected) {
        cardClass = "bg-gradient-to-br from-red-400 to-red-500 rounded-3xl p-6 shadow-xl border-4 border-red-300 transition-all duration-300 ";
      } else {
        cardClass = "bg-gradient-to-br from-gray-400 to-gray-500 rounded-3xl p-6 shadow-xl border-4 border-gray-300 transition-all duration-300 opacity-60 ";
      }
    }

    return (
      <div
        onClick={() => !showAnswer && handleAnswer(index)}
        className={cardClass}
      >
        <div className="text-center text-white">
          {/* Flag */}
          <div className="mb-4">
            <div className="w-24 h-16 mx-auto border-2 border-white shadow-lg rounded overflow-hidden">
              <img 
                src={country.flag} 
                alt={`Flag of ${country.nameEnglish}`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = '/pics/flags/default.svg';
                }}
              />
            </div>
          </div>

          {/* Country Name */}
          <h3 className="text-2xl font-bold mb-2">{country.nameEnglish}</h3>
          <p className="text-lg mb-4" dir="rtl" lang="ar">{country.nameArabic}</p>

          {/* Audio Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              playCountryAudio(country, index);
            }}
            disabled={audioPlaying === index}
            className={`mb-4 px-4 py-2 rounded-full font-bold text-sm transition-all duration-200 flex items-center gap-2 mx-auto ${
              audioPlaying === index
                ? 'bg-yellow-600 cursor-not-allowed'
                : 'bg-yellow-400 text-yellow-800 hover:bg-yellow-300 hover:scale-110'
            }`}
          >
            <Volume2 size={16} />
            {audioPlaying === index ? 'Playing...' : 'Listen'}
          </button>

          {/* Population - hidden until answer is revealed */}
          {showAnswer && (
            <div className="bg-white/20 rounded-xl p-3 backdrop-blur-sm">
              <p className="text-sm mb-1">Population:</p>
              <p className="text-xl font-bold">{country.population.toLocaleString()}</p>
            </div>
          )}

          {/* Question mark when not answered */}
          {!showAnswer && (
            <div className="text-4xl mb-2">❓</div>
          )}
        </div>
      </div>
    );
  });

  // Loading state
  if (countriesLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-sm w-full">
          <div className="text-center">
            <div className="text-6xl mb-4 animate-spin">🌍</div>
            <div className="text-teal-600 text-2xl font-bold animate-pulse">Loading Countries...</div>
            <div className="text-gray-600 text-lg mt-2">Getting ready for comparisons!</div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (countriesError || countries.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl">
          <div className="text-6xl mb-4">😢</div>
          <h2 className="text-3xl font-bold text-red-600 mb-4">Oops!</h2>
          <p className="text-gray-700 mb-6 text-lg">
            {countriesError?.message || 'Unable to load country data. Please try again.'}
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-2xl font-bold text-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
          >
            🔄 Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-400 via-teal-500 to-cyan-600 p-4">
      <AudioElements />
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-6xl font-black text-white mb-4 drop-shadow-2xl">
            🌍 Pop Quest 🌍
          </h1>
          <p className="text-xl md:text-2xl text-white font-bold opacity-90 drop-shadow-lg">
            Compare Arabic countries by population!
          </p>
        </div>

        {gamePhase === 'menu' && (
          <div className="max-w-2xl mx-auto">
            {/* Game Info */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8 border-4 border-yellow-300">
              <h2 className="text-3xl font-bold text-center mb-6 text-teal-600">
                🎯 How to Play
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="text-center bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl p-6">
                  <div className="text-4xl mb-3">🏳️</div>
                  <h3 className="font-bold text-lg mb-2">See Two Countries</h3>
                  <p className="text-gray-700">Each with flag and Arabic name</p>
                </div>
                <div className="text-center bg-gradient-to-br from-green-100 to-green-200 rounded-2xl p-6">
                  <div className="text-4xl mb-3">🔊</div>
                  <h3 className="font-bold text-lg mb-2">Hear Arabic Names</h3>
                  <p className="text-gray-700">Click to hear pronunciation</p>
                </div>
                <div className="text-center bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl p-6">
                  <div className="text-4xl mb-3">📊</div>
                  <h3 className="font-bold text-lg mb-2">Pick the Bigger</h3>
                  <p className="text-gray-700">Which has more people?</p>
                </div>
                <div className="text-center bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl p-6">
                  <div className="text-4xl mb-3">🔥</div>
                  <h3 className="font-bold text-lg mb-2">Build Streaks</h3>
                  <p className="text-gray-700">Get bonus points for streaks!</p>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={startGame}
                  className="px-12 py-6 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-3xl font-black text-3xl hover:shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white"
                >
                  🚀 START COMPARING! 🚀
                </button>
              </div>
            </div>

            {/* Best Score Display */}
            {bestScores?.level1 && (
              <div className="bg-white rounded-3xl shadow-xl p-6 border-4 border-purple-300">
                <h3 className="text-2xl font-bold text-center mb-4 text-purple-600">🏆 Your Best Game</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-indigo-600">{bestScores.level1.totalScore}</p>
                    <p className="text-sm text-gray-600">Best Score</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-600">{bestScores.level1.bestStreak}</p>
                    <p className="text-sm text-gray-600">Best Streak</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-orange-600">{bestScores.level1.accuracy}%</p>
                    <p className="text-sm text-gray-600">Best Accuracy</p>
                  </div>
                  <div className="flex justify-center">
                    <StarDisplay count={bestScores.level1.stars} />
                  </div>
                </div>
              </div>
            )}

            {/* Controls */}
            <div className="flex gap-4 justify-center mt-6">
              <Link to="/">
              <button 
                className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                🔙 Main Menu
              </button>
              </Link>
              <button 
                onClick={() => setShowInstructions(true)}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                📖 Instructions
              </button>
              <button 
                onClick={resetProgress}
                className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                🗑️ Reset Progress
              </button>
            </div>
          </div>
        )}

        {gamePhase === 'playing' && (
          <div className="max-w-4xl mx-auto">
            {/* Game Stats */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 mb-8 shadow-xl">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
                <div className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl p-4 text-white">
                  <div className="text-3xl mb-1">🎯</div>
                  <p className="font-bold text-xl">{score}</p>
                  <p className="text-xs font-bold">Score</p>
                </div>
                <div className="bg-gradient-to-br from-green-400 to-green-600 rounded-xl p-4 text-white">
                  <div className="text-3xl mb-1">✅</div>
                  <p className="font-bold text-xl">{totalCorrect}</p>
                  <p className="text-xs font-bold">Correct</p>
                </div>
                <div className="bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl p-4 text-white">
                  <div className="text-3xl mb-1">🔥</div>
                  <p className="font-bold text-xl">{currentStreak}</p>
                  <p className="text-xs font-bold">Streak</p>
                </div>
                <div className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-xl p-4 text-white">
                  <div className="text-3xl mb-1">🏆</div>
                  <p className="font-bold text-xl">{bestStreak}</p>
                  <p className="text-xs font-bold">Best</p>
                </div>
                <div className="bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-xl p-4 text-white">
                  <StarDisplay count={stars} size="text-lg" />
                  <p className="text-xs font-bold mt-1">Stars</p>
                </div>
              </div>
              
              {currentStreak >= 5 && (
                <div className="mt-4 text-center bg-gradient-to-r from-orange-400/80 to-red-400/80 rounded-xl p-3">
                  <p className="text-white font-bold text-lg animate-pulse">
                    🔥 {currentStreak} in a row! Amazing streak! 🔥
                  </p>
                </div>
              )}
            </div>

            {/* Question */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8 border-4 border-yellow-300">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  Which country has a BIGGER population? 🤔
                </h2>
                <p className="text-gray-600 text-lg">Click on the country you think has more people!</p>
                <p className="text-sm text-gray-500 mt-2">💡 Tap the 🔊 button to hear the Arabic names</p>
              </div>

              {/* Countries Comparison */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
                <CountryCard 
                  country={currentCountries[0]} 
                  index={0}
                  isSelected={selectedAnswer === 0}
                  isCorrect={showResult && currentCountries[0] && currentCountries[1] && currentCountries[0].population >= currentCountries[1].population}
                  showAnswer={showResult}
                />
                <CountryCard 
                  country={currentCountries[1]} 
                  index={1}
                  isSelected={selectedAnswer === 1}
                  isCorrect={showResult && currentCountries[0] && currentCountries[1] && currentCountries[1].population >= currentCountries[0].population}
                  showAnswer={showResult}
                />
              </div>

              {/* VS Divider */}
              <div className="flex items-center justify-center my-8">
                <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white font-black text-3xl px-8 py-4 rounded-full shadow-xl border-4 border-white">
                  VS
                </div>
              </div>

              {/* Feedback */}
              {feedback && (
                <div className={`text-center mb-6 p-6 rounded-2xl border-4 ${
                  feedback === 'correct' 
                    ? 'text-green-600 bg-green-100 border-green-300' 
                    : 'text-red-600 bg-red-100 border-red-300'
                }`}>
                  <div className={`text-6xl mb-2 ${
                    feedback === 'correct' ? 'animate-bounce' : 'animate-pulse'
                  }`}>
                    {feedback === 'correct' ? '🎉' : '❌'}
                  </div>
                  <p className="text-2xl font-bold">
                    {feedback === 'correct' ? 
                      'Fantastic! You got it right!' : 
                      'Oops! Better luck next time!'
                    }
                  </p>
                  {showResult && currentCountries[0] && currentCountries[1] && (
                    <p className="text-lg mt-2">
                      {currentCountries[0].population >= currentCountries[1].population ? 
                        `${currentCountries[0].nameEnglish} has more people!` :
                        `${currentCountries[1].nameEnglish} has more people!`
                      }
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-xl">
              <div className="flex gap-4 justify-center">
                <button 
                  onClick={goToMenu}
                  className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Home size={20} />
                  Menu
                </button>
                <button 
                  onClick={endGame}
                  className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Trophy size={20} />
                  End Game
                </button>
                <button 
                  onClick={() => setShowGameStats(true)}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center gap-2"
                >
                  <Target size={20} />
                  Stats
                </button>
              </div>
            </div>
          </div>
        )}

        {gamePhase === 'gameOver' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl shadow-2xl p-8 border-4 border-yellow-300">
              <div className="text-center">
                <div className="text-8xl mb-6">🎉</div>
                <h2 className="text-4xl font-bold text-teal-600 mb-4">Game Complete!</h2>
                
                <div className="mb-8">
                  <StarDisplay count={stars} size="text-4xl" />
                  <p className="text-3xl font-bold text-indigo-600 mt-4">Final Score: {score}</p>
                </div>

                <div className="grid grid-cols-2 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-green-100 to-green-200 rounded-2xl p-6">
                    <p className="text-2xl font-bold text-green-600">{totalCorrect}</p>
                    <p className="text-gray-700">Correct Answers</p>
                  </div>
                  <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl p-6">
                    <p className="text-2xl font-bold text-orange-600">{bestStreak}</p>
                    <p className="text-gray-700">Best Streak</p>
                  </div>
                  <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl p-6">
                    <p className="text-2xl font-bold text-blue-600">
                      {totalQuestions > 0 ? Math.round((totalCorrect / totalQuestions) * 100) : 0}%
                    </p>
                    <p className="text-gray-700">Accuracy</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl p-6">
                    <p className="text-2xl font-bold text-purple-600">{totalQuestions}</p>
                    <p className="text-gray-700">Total Questions</p>
                  </div>
                </div>

                <div className="flex gap-4 justify-center">
                  <button 
                    onClick={startGame}
                    className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    🔄 Play Again
                  </button>
                  <button 
                    onClick={goToMenu}
                    className="px-8 py-4 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                  >
                    🏠 Main Menu
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Celebration Effects */}
        {celebrations && (
          <div className="fixed inset-0 pointer-events-none z-40">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-9xl animate-bounce">🎉</div>
            </div>
          </div>
        )}

        {sparkles && (
          <div className="fixed inset-0 pointer-events-none z-40">
            {[...Array(20)].map((_, i) => (
              <div
                key={`sparkle-${i}`}
                className="absolute animate-ping text-yellow-400 text-2xl"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 2}s`
                }}
              >
                ✨
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Instructions Modal */}
      {showInstructions && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border-4 border-teal-300">
            <div className="p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold text-teal-600">📖 How to Master the Game!</h2>
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="text-gray-500 hover:text-gray-700 text-3xl font-bold w-10 h-10 flex items-center justify-center"
                >
                  ×
                </button>
              </div>
              
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-100 to-blue-200 p-6 rounded-2xl border-4 border-blue-300">
                  <h3 className="font-bold text-blue-800 mb-3 text-xl">🎯 Your Mission</h3>
                  <p className="text-blue-700 text-lg">Compare two Arabic countries and pick the one with the bigger population!</p>
                </div>
                
                <div className="bg-gradient-to-r from-green-100 to-green-200 p-6 rounded-2xl border-4 border-green-300">
                  <h3 className="font-bold text-green-800 mb-3 text-xl">🎮 How to Play</h3>
                  <ul className="list-disc list-inside text-green-700 space-y-2 text-lg">
                    <li>Look at two country cards with flags and names</li>
                    <li>Click the 🔊 button to hear the Arabic pronunciation</li>
                    <li>Guess which country has more people</li>
                    <li>Click on your choice to see if you're right!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-orange-100 to-orange-200 p-6 rounded-2xl border-4 border-orange-300">
                  <h3 className="font-bold text-orange-800 mb-3 text-xl">🏆 Scoring System</h3>
                  <ul className="list-disc list-inside text-orange-700 space-y-2 text-lg">
                    <li>Earn 20 points for each correct answer</li>
                    <li>Build streaks for bonus multipliers!</li>
                    <li>Collect stars: ⭐ at 10, ⭐⭐ at 25, ⭐⭐⭐ at 50 correct</li>
                    <li>Special bonuses for long streaks!</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-6 rounded-2xl border-4 border-purple-300">
                  <h3 className="font-bold text-purple-800 mb-3 text-xl">💡 Pro Tips</h3>
                  <ul className="list-disc list-inside text-purple-700 space-y-2 text-lg">
                    <li>Use the audio to learn Arabic country names!</li>
                    <li>Think about what you know about each country</li>
                    <li>Big countries often have more people (but not always!)</li>
                    <li>Keep playing to beat your high score and streak!</li>
                  </ul>
                </div>
              </div>
              
              <div className="mt-8 text-center">
                <button 
                  onClick={() => setShowInstructions(false)}
                  className="px-8 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-2xl font-bold text-xl hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Got it! Let's Compare! 🌍
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 border-4 border-red-300">
            <div className="text-center">
              <div className="text-8xl mb-6">⚠️</div>
              <h2 className="text-3xl font-bold text-red-600 mb-4">Reset Everything?</h2>
              <p className="text-gray-700 mb-8 text-lg">
                This will delete all your scores and progress. This action cannot be undone!
              </p>
              <div className="flex gap-4 justify-center">
                <button 
                  onClick={() => setShowResetConfirmation(false)}
                  className="px-6 py-3 bg-gradient-to-r from-gray-500 to-gray-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Cancel
                </button>
                <button 
                  onClick={confirmReset}
                  className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-200"
                >
                  Yes, Reset Everything
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BiggerSmallerCountryGame;